import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, ShieldCheck, Plus, Power, LogOut, Store, KeyRound, Mail } from 'lucide-react';
import { LicenseKeysPanel } from '../components/LicenseKeysPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tenant`;
const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = 'sa_token';
const sa = axios.create();  // instância isolada (sem o interceptor global de refresh do AuthContext)

const hdr = (t) => ({ headers: { 'X-Super-Admin-Token': t } });

export default function SuperAdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TKEY) || '');
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', cnpj: '', slug: '', admin_email: '', admin_password: '' });
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState('lojas');

  useEffect(() => { if (token) checkSession(token); /* eslint-disable-next-line */ }, []);

  const checkSession = async (t) => {
    try {
      const { data } = await sa.get(`${API}/super/me`, hdr(t));
      setInfo(data); setAuthed(true); loadTenants(t);
    } catch { setAuthed(false); localStorage.removeItem(TKEY); }
  };

  const login = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const { data } = await sa.post(`${API}/super/login`, { password });
      localStorage.setItem(TKEY, data.token); setToken(data.token);
      await checkSession(data.token);
    } catch (err) { setError(err.response?.data?.detail || 'Falha no login'); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await sa.post(`${API}/super/logout`, {}, hdr(token)); } catch {}
    localStorage.removeItem(TKEY); setToken(''); setAuthed(false);
  };

  const loadTenants = async (t) => {
    try { const { data } = await sa.get(`${API}/list`, hdr(t || token)); setTenants(data.tenants || []); setInfo((i) => ({ ...(i || {}), ...data })); }
    catch (err) { if (err.response?.status === 401) logout(); }
  };

  const provision = async (e) => {
    e.preventDefault(); setError(''); setMsg(''); setBusy(true);
    try {
      const { data } = await sa.post(`${API}/provision`, form, hdr(token));
      setMsg(`Loja "${data.tenant.name}" criada! Banco: ${data.tenant.db_name} · admin: ${data.tenant.admin_email}`);
      setForm({ name: '', cnpj: '', slug: '', admin_email: '', admin_password: '' });
      loadTenants();
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao criar loja'); }
    finally { setBusy(false); }
  };

  const toggle = async (slug, active) => {
    try { await sa.post(`${API}/${slug}/status`, { active }, hdr(token)); loadTenants(); }
    catch (err) { setError(err.response?.data?.detail || 'Erro ao alterar status'); }
  };

  const setSubscription = async (slug, status) => {
    try { await sa.post(`${API}/${slug}/subscription`, { status }, hdr(token)); loadTenants(); }
    catch (err) { setError(err.response?.data?.detail || 'Erro ao alterar assinatura'); }
  };

  const [resetSlug, setResetSlug] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');
  const doReset = async (e) => {
    e.preventDefault(); setResetError(''); setResetMsg('');
    try {
      const { data } = await sa.post(`${API}/${resetSlug}/reset-admin-password`, { new_password: resetPwd }, hdr(token));
      setResetMsg(`Senha do admin (${data.email}) redefinida com sucesso!`);
      setResetPwd('');
      setTimeout(() => { setResetSlug(null); setResetMsg(''); }, 2000);
    } catch (err) { setResetError(err.response?.data?.detail || 'Erro ao redefinir senha'); }
  };

  // --- Recadastro de e-mail do admin ---
  const [emailSlug, setEmailSlug] = useState(null);
  const [emailCur, setEmailCur] = useState('');
  const [emailNew, setEmailNew] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const openEmail = async (slug) => {
    setEmailSlug(slug); setEmailNew(''); setEmailMsg(''); setEmailError(''); setEmailCur(''); setEmailLoading(true);
    try {
      const { data } = await sa.get(`${API}/${slug}/admin`, hdr(token));
      setEmailCur(data.admin_email || '');
    } catch (err) { setEmailError(err.response?.data?.detail || 'Erro ao carregar o e-mail atual'); }
    finally { setEmailLoading(false); }
  };
  const doChangeEmail = async (e) => {
    e.preventDefault(); setEmailError(''); setEmailMsg('');
    try {
      const { data } = await sa.post(`${API}/${emailSlug}/admin-email`, { new_email: emailNew, current_email: emailCur || undefined }, hdr(token));
      setEmailMsg(`E-mail recadastrado: ${data.new_email}`);
      setEmailNew('');
      loadTenants();
      setTimeout(() => { setEmailSlug(null); setEmailMsg(''); }, 1800);
    } catch (err) { setEmailError(err.response?.data?.detail || 'Erro ao recadastrar e-mail'); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="super-admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-xl p-8 space-y-4">
          <div className="flex items-center gap-2 text-emerald-400"><ShieldCheck size={22} /><h1 className="text-xl font-black text-white">Super Admin</h1></div>
          <p className="text-slate-400 text-sm">Painel de controle das lojas (control plane).</p>
          <div className="space-y-1">
            <Label className="text-slate-300">Senha</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="super-password" autoFocus />
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="super-login-btn">{busy ? 'Entrando...' : 'Entrar'}</Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-8" data-testid="super-admin-panel">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck className="text-emerald-400" size={26} /><h1 className="text-3xl font-black">Painel Super Admin</h1></div>
          <Button variant="outline" onClick={logout} className="border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:text-white" data-testid="super-logout-btn"><LogOut size={16} className="mr-2" />Sair</Button>
        </div>

        {info && !info.multi_tenant_enabled && (
          <Alert className="bg-amber-950/40 border-amber-800 text-amber-200">
            <AlertDescription>Modo multi-loja <b>desativado</b> (sem <code>TENANT_BASE_DOMAIN</code>). As lojas ficam registradas e prontas; o roteamento por subdomínio será ligado na Fase 3.</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2 border-b border-slate-800" data-testid="super-tabs">
          {[['lojas', 'Lojas', Store], ['licencas', 'Chaves de Licença', KeyRound]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === k ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-white'}`} data-testid={`super-tab-${k}`}>
              <Icon size={16} /> {l}
            </button>
          ))}
        </div>

        {tab === 'licencas' && <LicenseKeysPanel api={{ get: (p, c) => sa.get(`${API_ROOT}${p}`, c), post: (p, b, c) => sa.post(`${API_ROOT}${p}`, b, c) }} hdr={hdr(token)} />}

        <div className={tab === 'lojas' ? 'bg-slate-900 border border-slate-800 rounded-xl p-6' : 'hidden'}>
          <div className="flex items-center gap-2 mb-4 text-emerald-400"><Plus size={18} /><h2 className="text-lg font-bold text-white">Criar nova loja (1 clique)</h2></div>
          <form onSubmit={provision} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="text-slate-300">Nome da empresa *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-slate-800 border-slate-700 text-white" data-testid="prov-name" placeholder="Ex: Verdurão Centro" /></div>
            <div className="space-y-1"><Label className="text-slate-300">CNPJ</Label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="prov-cnpj" placeholder="00.000.000/0001-00" /></div>
            <div className="space-y-1"><Label className="text-slate-300">Subdomínio *</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} required className="bg-slate-800 border-slate-700 text-white" data-testid="prov-slug" placeholder="verdurao-centro" /><p className="text-[11px] text-slate-500">Vira: <b>{form.slug || 'subdominio'}</b>.seudominio.com</p></div>
            <div className="space-y-1"><Label className="text-slate-300">E-mail do admin *</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required className="bg-slate-800 border-slate-700 text-white" data-testid="prov-email" placeholder="dono@loja.com" /></div>
            <div className="space-y-1"><Label className="text-slate-300">Senha do admin *</Label><Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required className="bg-slate-800 border-slate-700 text-white" data-testid="prov-password" placeholder="mínimo 6 caracteres" /></div>
            <div className="flex items-end"><Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="prov-submit"><Building2 size={16} className="mr-2" />{busy ? 'Criando...' : 'Criar Loja'}</Button></div>
          </form>
          {msg && <div className="mt-3"><Alert className="bg-emerald-950/40 border-emerald-800 text-emerald-200"><AlertDescription data-testid="prov-success">{msg}</AlertDescription></Alert></div>}
          {error && <div className="mt-3"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
        </div>

        <div className={tab === 'lojas' ? 'bg-slate-900 border border-slate-800 rounded-xl overflow-hidden' : 'hidden'}>
          <div className="flex items-center gap-2 p-4 border-b border-slate-800 text-emerald-400"><Store size={18} /><h2 className="text-lg font-bold text-white">Lojas cadastradas ({tenants.length})</h2></div>
          <table className="w-full text-sm" data-testid="tenants-table">
            <thead className="bg-slate-800/60 text-slate-400"><tr>
              <th className="text-left px-4 py-2">Loja</th><th className="text-left px-4 py-2">Subdomínio</th><th className="text-left px-4 py-2">Admin (e-mail)</th><th className="text-left px-4 py-2">Banco</th><th className="text-center px-4 py-2">Assinatura</th><th className="text-center px-4 py-2">Status</th><th className="text-right px-4 py-2">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800">
              {tenants.map((t) => (
                <tr key={t.slug} data-testid={`tenant-row-${t.slug}`}>
                  <td className="px-4 py-3 font-medium text-white">{t.name}{t.is_default && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">PADRÃO</span>}</td>
                  <td className="px-4 py-3 text-slate-300">{t.slug}</td>
                  <td className="px-4 py-3 text-slate-300" data-testid={`admin-email-${t.slug}`}>{t.admin_email || <span className="text-slate-600">—</span>}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{t.db_name}</td>
                  <td className="px-4 py-3 text-center" data-testid={`sub-status-${t.slug}`}>
                    {t.is_default ? <span className="text-slate-500 text-xs">—</span> : (
                      t.subscription_status === 'vencida'
                        ? <span className="text-red-400 font-semibold">Vencida</span>
                        : <span className="text-emerald-400 font-semibold">Em dia</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">{t.active ? <span className="text-emerald-400 font-semibold">Ativa</span> : <span className="text-red-400 font-semibold">Suspensa</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openEmail(t.slug)} className="border-slate-700 text-cyan-400" data-testid={`change-email-${t.slug}`}>
                        <Mail size={14} className="mr-1" />E-mail
                      </Button>
                      {!t.is_default && (
                        <>
                          {t.subscription_status === 'vencida'
                            ? <Button size="sm" variant="outline" onClick={() => setSubscription(t.slug, 'em_dia')} className="border-slate-700 text-emerald-400" data-testid={`sub-regularize-${t.slug}`}>Regularizar</Button>
                            : <Button size="sm" variant="outline" onClick={() => setSubscription(t.slug, 'vencida')} className="border-slate-700 text-amber-400" data-testid={`sub-expire-${t.slug}`}>Vencer</Button>}
                          <Button size="sm" variant="outline" onClick={() => toggle(t.slug, !t.active)} className={`border-slate-700 ${t.active ? 'text-red-400' : 'text-emerald-400'}`} data-testid={`toggle-${t.slug}`}>
                            <Power size={14} className="mr-1" />{t.active ? 'Suspender' : 'Ativar'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setResetSlug(t.slug); setResetPwd(''); setResetMsg(''); setResetError(''); }} className="border-slate-700 text-blue-400" data-testid={`reset-pwd-${t.slug}`}>
                            <KeyRound size={14} className="mr-1" />Senha
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Nenhuma loja ainda.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {resetSlug && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="reset-pwd-modal">
          <form onSubmit={doReset} className="bg-slate-900 border border-slate-800 rounded-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-blue-400"><KeyRound size={18} /><h3 className="text-lg font-bold text-white">Redefinir senha do admin</h3></div>
            <p className="text-slate-400 text-sm">Loja: <b className="text-slate-200">{resetSlug}</b></p>
            <div className="space-y-1">
              <Label className="text-slate-300">Nova senha (mín. 6)</Label>
              <Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="reset-pwd-input" autoFocus />
            </div>
            {resetMsg && <Alert className="bg-emerald-950/40 border-emerald-800 text-emerald-200"><AlertDescription data-testid="reset-pwd-success">{resetMsg}</AlertDescription></Alert>}
            {resetError && <Alert variant="destructive"><AlertDescription>{resetError}</AlertDescription></Alert>}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { setResetSlug(null); setResetError(''); }} className="border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700">Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="reset-pwd-confirm">Redefinir</Button>
            </div>
          </form>
        </div>
      )}
      {emailSlug && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="change-email-modal">
          <form onSubmit={doChangeEmail} className="bg-slate-900 border border-slate-800 rounded-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-cyan-400"><Mail size={18} /><h3 className="text-lg font-bold text-white">Recadastrar e-mail do admin</h3></div>
            <p className="text-slate-400 text-sm">Loja: <b className="text-slate-200">{emailSlug}</b></p>
            <div className="space-y-1">
              <Label className="text-slate-300">E-mail atual</Label>
              <Input value={emailLoading ? 'Carregando...' : emailCur} readOnly className="bg-slate-800/60 border-slate-700 text-slate-400" data-testid="email-current" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">Novo e-mail</Label>
              <Input type="email" value={emailNew} onChange={(e) => setEmailNew(e.target.value)} placeholder="novo@loja.com" className="bg-slate-800 border-slate-700 text-white" data-testid="email-new-input" autoFocus required />
            </div>
            {emailMsg && <Alert className="bg-emerald-950/40 border-emerald-800 text-emerald-200"><AlertDescription data-testid="email-success">{emailMsg}</AlertDescription></Alert>}
            {emailError && <Alert variant="destructive"><AlertDescription>{emailError}</AlertDescription></Alert>}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { setEmailSlug(null); setEmailError(''); }} className="border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700">Cancelar</Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" data-testid="email-confirm">Recadastrar</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
